import type { OrganizationDutyConfiguration } from "./types";
import type { CreatePlatformUserRequest, PlatformUser } from "../features/platform-users/types";
import type { DutyHistory, DutyHistoryOptions } from "../features/duty-history/types";
import type { StaffReportingView } from "../features/organizations/reporting/reportingTypes";
import type { MessagePage, MessageDetail } from "../features/whatsapp/types";
import type { LocationPocView, LocationPocAssignment, LocationPocPreview } from "../features/organizations/locationPocTypes";
import type { OrganizationProjectPocView } from "../features/organizations/OrganizationProjectPoc";
import type { ConcernPage, ConcernReport, ConcernStatus } from "../features/concerns/types";
import type {
  DirectoryUsersResponse,
  BlockerReasonsResponse,
  UserOrganizationOption,
  UserDetailsResponse,
  OrganizationDetailsResponse,
  OrganizationConnectionsResponse,
  ApiFailure,
  ApiSuccess,
  AdminFileUploadInitResponse,
  AdminImportType,
  AdminMasterDataUploadResponse,
  OrganizationSettingsResponse,
  OrganizationStatus,
  OrganizationType,
  OrganizationCreationAddress,
  OrganizationAddressInput,
  OrganizationAddress,
  OrganizationLocationInput,
  RegistrationType,
  UserStatus,
  RefreshSessionResponse,
  SendOtpResponse,
  SignupOtpSettingsResponse,
  TechOpsConnectionsResponse,
  TechOpsDashboardResponse,
  TechOpsMasterDataResponse,
  TechOpsOrganizationsResponse,
  TechOpsProjectsResponse,
  TechAdminAuditLogResponse,
  TechAdminWorkspaceResponse,
  UpdateOrganizationSettingRequest,
  UpdateWorkProgressSettingsRequest,
  VerifyOtpResponse,
  WorkProgressSettingsResponse
} from "./types";
import { getStoredSession, removeStoredSession, storeSession } from "./session";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "https://api.coprint.in/api/v1";

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  auth?: boolean;
};

type ApiEnvelope<T> = ApiSuccess<T> | ApiFailure;

type ListOrganizationsParams = {
  search?: string;
  status?: string;
  type?: string;
};

type ListConnectionsParams = {
  search?: string;
  status?: string;
};

type ListProjectsParams = {
  owner_organization_id?: string;
  status?: string;
};

type SearchParams = {
  search?: string;
};

type AuditParams = {
  search?: string;
  action?: string;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

let refreshPromise: Promise<string | null> | null = null;

async function parseEnvelope<T>(response: Response): Promise<ApiEnvelope<T>> {
  const text = await response.text();
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(response.status, "INVALID_API_RESPONSE", "The backend returned a non-JSON response.");
  }
}

async function refreshAccessToken() {
  const session = getStoredSession();
  if (!session?.refreshToken) {
    removeStoredSession();
    return null;
  }
  const response = await fetch(`${API_BASE_URL}/auth/session/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: session.refreshToken })
  });
  const payload = await parseEnvelope<RefreshSessionResponse>(response);
  if (response.ok && payload.success) {
    storeSession({ accessToken: payload.data.access_token, refreshToken: payload.data.refresh_token });
    return payload.data.access_token;
  }
  if (response.status === 401 || response.status === 403) {
    removeStoredSession();
    return null;
  }
  const failure = payload as ApiFailure;
  throw new ApiError(response.status, failure.error.code, failure.error.message);
}

async function sendRequest<T>(path: string, options: RequestOptions, accessToken: string | null) {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: options.method ?? "GET",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body)
    });
  } catch {
    throw new ApiError(0, "NETWORK_UNAVAILABLE", "Could not reach the Coprint backend.");
  }
  return { response, payload: await parseEnvelope<T>(response) };
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const session = getStoredSession();
  const accessToken = options.auth === false ? null : session?.accessToken ?? null;
  const result = await sendRequest<T>(path, options, accessToken);
  if (result.response.ok && result.payload.success) {
    return result.payload.data;
  }
  if (result.response.status === 401 && options.auth !== false && accessToken) {
    refreshPromise ??= refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
    const refreshed = await refreshPromise;
    if (refreshed) {
      const retry = await sendRequest<T>(path, options, refreshed);
      if (retry.response.ok && retry.payload.success) {
        return retry.payload.data;
      }
      const failure = retry.payload as ApiFailure;
      throw new ApiError(retry.response.status, failure.error.code, failure.error.message);
    }
  }
  const failure = result.payload as ApiFailure;
  throw new ApiError(result.response.status, failure.error.code, failure.error.message);
}

function withQuery(path: string, params: Record<string, string | undefined>) {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value && value !== "ALL") {
      searchParams.set(key, value);
    }
  }
  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
}

export const api = {
  getOrganizationDutySettings(organizationId: string) {
    return apiRequest<OrganizationDutyConfiguration>(`/platform/organizations/${encodeURIComponent(organizationId)}/duty-settings`);
  },
  updateOrganizationDutySettings(organizationId: string, body: OrganizationDutyConfiguration) {
    return apiRequest<OrganizationDutyConfiguration>(`/platform/organizations/${encodeURIComponent(organizationId)}/duty-settings`, { method: "PUT", body });
  },
  listConcerns(status?: ConcernStatus, page = 0) {
    return apiRequest<ConcernPage>(withQuery("/platform/tech-ops/concerns", { status, page: String(page) }));
  },
  getConcern(reportId: string) {
    return apiRequest<ConcernReport>(`/platform/tech-ops/concerns/${encodeURIComponent(reportId)}`);
  },
  reviewConcern(reportId: string, status: ConcernStatus, notes: string, version: number) {
    return apiRequest<ConcernReport>(`/platform/tech-ops/concerns/${encodeURIComponent(reportId)}`, { method: "PATCH", body: { status, notes, version } });
  },
  listAdminBlockerReasons(organizationId?: string) {
    return apiRequest<BlockerReasonsResponse>(`/platform/tech-admin/blocker-reasons${organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ""}`);
  },
  saveAdminBlockerReason(body: { task_type: string; name: string; description: string | null; active: boolean }, reasonId?: string, organizationId?: string) {
    return apiRequest<BlockerReasonsResponse>(`/platform/tech-admin/blocker-reasons${reasonId ? `/${encodeURIComponent(reasonId)}` : ""}${organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ""}`, { method: reasonId ? "PUT" : "POST", body });
  },
  removeAdminBlockerReason(reasonId: string, organizationId?: string) {
    return apiRequest<BlockerReasonsResponse>(`/platform/tech-admin/blocker-reasons/${encodeURIComponent(reasonId)}${organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ""}`, { method: "DELETE" });
  },

  listWhatsAppMessages(params: { search?: string; status?: string; purpose?: string; from?: string; to?: string; page?: string }) {
    return apiRequest<MessagePage>(withQuery("/platform/tech-ops/whatsapp/messages", params));
  },
  getWhatsAppMessage(id: string) {
    return apiRequest<MessageDetail>(`/platform/tech-ops/whatsapp/messages/${encodeURIComponent(id)}`);
  },
  sendOtp(mobileNumber: string) {
    return apiRequest<SendOtpResponse>("/auth/platform/otp/send", {
      method: "POST",
      auth: false,
      body: { mobile_number: mobileNumber }
    });
  },
  verifyOtp(otpRequestId: string, mobileNumber: string, otp: string) {
    return apiRequest<VerifyOtpResponse>("/auth/otp/verify", {
      method: "POST",
      auth: false,
      body: { otp_request_id: otpRequestId, mobile_number: mobileNumber, otp }
    });
  },
  getDashboard() {
    return apiRequest<TechOpsDashboardResponse>("/platform/tech-ops/dashboard");
  },
  listUsers(params: { search?: string; status?: string; is_test_user?: string; organization_id?: string; role?: string; page?: string } = {}) {
    return apiRequest<DirectoryUsersResponse>(withQuery("/platform/tech-ops/users", params));
  },
  listUserOrganizations() {
    return apiRequest<UserOrganizationOption[]>("/platform/tech-ops/users/organization-options");
  },
  getUser(userId: string) {
    return apiRequest<UserDetailsResponse>(`/platform/tech-ops/users/${encodeURIComponent(userId)}`);
  },
  updateOrganizationLive(organizationId: string, isLive: boolean) {
    return apiRequest<{ organization_id: string; is_live: boolean }>(`/platform/organizations/${encodeURIComponent(organizationId)}/live`, {
      method: "PUT", body: { is_live: isLive }
    });
  },
  getOrganization(organizationId: string) {
    return apiRequest<OrganizationDetailsResponse>(`/platform/tech-ops/organizations/${encodeURIComponent(organizationId)}`);
  },
  getOrganizationConnections(organizationId: string) {
    return apiRequest<OrganizationConnectionsResponse>(`/platform/tech-ops/organizations/${encodeURIComponent(organizationId)}/connections`);
  },
  getOrganizationReporting(organizationId: string) {
    return apiRequest<StaffReportingView>(`/platform/organizations/${encodeURIComponent(organizationId)}/reporting`);
  },
  setOrganizationReportingManager(organizationId: string, memberId: string, managerId: string) {
    return apiRequest<StaffReportingView>(`/platform/organizations/${encodeURIComponent(organizationId)}/reporting/users/${encodeURIComponent(memberId)}/reports-to`, {
      method: "PATCH", body: { manager_organization_user_id: managerId }
    });
  },
  clearOrganizationReportingManager(organizationId: string, memberId: string) {
    return apiRequest<StaffReportingView>(`/platform/organizations/${encodeURIComponent(organizationId)}/reporting/users/${encodeURIComponent(memberId)}/reports-to`, { method: "DELETE" });
  },
  getLocationPocs(organizationId: string) {
    return apiRequest<LocationPocView>(`/platform/organizations/${encodeURIComponent(organizationId)}/pocs`);
  },
  getOrganizationProjectPoc(organizationId: string) {
    return apiRequest<OrganizationProjectPocView>(`/platform/organizations/${encodeURIComponent(organizationId)}/project-poc`);
  },
  updateOrganizationProjectPoc(organizationId: string, userId: string, expectedUpdatedAt: string) {
    return apiRequest<OrganizationProjectPocView>(`/platform/organizations/${encodeURIComponent(organizationId)}/project-poc`, {
      method: "PUT", body: { user_id: userId, expected_updated_at: expectedUpdatedAt }
    });
  },
  previewLocationPocs(organizationId: string, csv: string) {
    return apiRequest<LocationPocPreview>(`/platform/organizations/${encodeURIComponent(organizationId)}/pocs/preview`, { method: "POST", body: { csv } });
  },
  updateLocationPocs(organizationId: string, assignments: LocationPocAssignment[]) {
    return apiRequest<LocationPocView>(`/platform/organizations/${encodeURIComponent(organizationId)}/pocs`, { method: "PUT", body: { assignments } });
  },
  listOrganizationAddresses(organizationId: string) {
    return apiRequest<OrganizationAddress[]>(`/platform/tech-ops/organizations/${encodeURIComponent(organizationId)}/addresses`);
  },
  addOrganizationAddress(organizationId: string, body: OrganizationAddressInput, shippingSameAsBilling = false) {
    return apiRequest<OrganizationAddress[]>(`/platform/tech-admin/organizations/${encodeURIComponent(organizationId)}/addresses${shippingSameAsBilling ? "?shippingSameAsBilling=true" : ""}`, { method: "POST", body });
  },
  updateOrganizationAddress(organizationId: string, addressId: string, body: OrganizationAddressInput) {
    return apiRequest<OrganizationAddress[]>(`/platform/tech-admin/organizations/${encodeURIComponent(organizationId)}/addresses/${encodeURIComponent(addressId)}`, { method: "PUT", body });
  },
  deleteOrganizationAddress(organizationId: string, addressId: string) {
    return apiRequest<OrganizationAddress[]>(`/platform/tech-admin/organizations/${encodeURIComponent(organizationId)}/addresses/${encodeURIComponent(addressId)}`, { method: "DELETE" });
  },
  addOrganizationUser(organizationId: string, body: { name: string; email: string; mobile_number: string; role_code: string }) {
    return apiRequest<{ user_id: string; name: string }>(`/platform/tech-admin/organizations/${encodeURIComponent(organizationId)}/users`, { method: "POST", body });
  },
  addOrganizationBillingAddress(organizationId: string, body: OrganizationCreationAddress, shippingSameAsBilling = false) {
    return apiRequest<OrganizationAddress[]>(`/platform/tech-admin/organizations/${encodeURIComponent(organizationId)}/addresses${shippingSameAsBilling ? "?shippingSameAsBilling=true" : ""}`, { method: "POST", body });
  },
  addOrganizationLocation(organizationId: string, body: OrganizationLocationInput) {
    return apiRequest<OrganizationAddress[]>(`/platform/tech-admin/organizations/${encodeURIComponent(organizationId)}/locations`, { method: "POST", body });
  },
  listOrganizationLocationUploads(organizationId: string) {
    return apiRequest<AdminMasterDataUploadResponse[]>(`/platform/tech-ops/organizations/${encodeURIComponent(organizationId)}/location-uploads`);
  },
  listOrganizations(params: ListOrganizationsParams = {}) {
    return apiRequest<TechOpsOrganizationsResponse>(withQuery("/platform/tech-ops/organizations", params));
  },
  listProjects(params: ListProjectsParams = {}) {
    return apiRequest<TechOpsProjectsResponse>(withQuery("/platform/tech-ops/projects", params));
  },
  listConnections(params: ListConnectionsParams = {}) {
    return apiRequest<TechOpsConnectionsResponse>(withQuery("/platform/tech-ops/connections", params));
  },
  getMasterData(params: SearchParams = {}) {
    return apiRequest<TechOpsMasterDataResponse>(withQuery("/platform/tech-ops/master-data", params));
  },
  getAdminWorkspace() {
    return apiRequest<TechAdminWorkspaceResponse>("/platform/tech-admin/workspace");
  },
  listAdminAuditEvents(params: AuditParams = {}) {
    return apiRequest<TechAdminAuditLogResponse>(withQuery("/platform/tech-admin/audit-log", params));
  },
  getWorkProgressSettings() {
    return apiRequest<WorkProgressSettingsResponse>("/platform/tech-admin/work-progress-settings");
  },
  getSignupOtpSettings() {
    return apiRequest<SignupOtpSettingsResponse>("/platform/tech-admin/signup-otp-settings");
  },
  updateSignupOtpSettings(enabled: boolean) {
    return apiRequest<SignupOtpSettingsResponse>("/platform/tech-admin/signup-otp-settings", {
      method: "PUT", body: { enabled }
    });
  },
  updateWorkProgressSettings(body: UpdateWorkProgressSettingsRequest) {
    return apiRequest<WorkProgressSettingsResponse>("/platform/tech-admin/work-progress-settings", {
      method: "PUT",
      body
    });
  },
  createAdminUser(body: { name: string; mobile_number: string; email?: string; registration_type: RegistrationType }) {
    return apiRequest("/platform/tech-admin/users", { method: "POST", body });
  },
  listPlatformUsers() {
    return apiRequest<PlatformUser[]>("/platform/tech-admin/platform-users");
  },
  createPlatformUser(body: CreatePlatformUserRequest) {
    return apiRequest<PlatformUser>("/platform/tech-admin/platform-users", { method: "POST", body });
  },
  deleteAdminUser(userId: string) {
    return apiRequest(`/platform/tech-admin/users/${encodeURIComponent(userId)}`, { method: "DELETE" });
  },
  updateAdminUser(userId: string, body: {
    name: string;
    email?: string;
    registration_type: RegistrationType;
    status: UserStatus;
    mobile_number?: string;
    is_test_user?: boolean;
    proxy_user_ids?: string[];
    role_code?: string;
    reports_to_membership_id?: string;
  }) {
    return apiRequest(`/platform/tech-admin/users/${userId}`, { method: "PATCH", body });
  },
  getGoogleMapsConfiguration() {
    return apiRequest<{ api_key: string }>("/vendor/google-maps/configuration");
  },
  getDutyHistoryOptions(organizationId: string) {
    return apiRequest<DutyHistoryOptions>(`/platform/organizations/${organizationId}/duty-history/options`);
  },
  getDutyHistory(organizationId: string, userId: string, date: string) {
    return apiRequest<DutyHistory>(`/platform/organizations/${organizationId}/duty-history?${new URLSearchParams({ user_id: userId, date })}`);
  },
  createAdminOrganization(body: { name: string; organization_type: OrganizationType; owner_user_id: string; addresses: OrganizationCreationAddress[] }) {
    return apiRequest("/platform/tech-admin/organizations", { method: "POST", body });
  },
  updateAdminOrganization(organizationId: string, body: { name: string; status: OrganizationStatus; organization_type?: OrganizationType; gstin?: string }) {
    return apiRequest(`/platform/tech-admin/organizations/${organizationId}`, { method: "PATCH", body });
  },
  getAdminOrganizationSettings(organizationId: string) {
    return apiRequest<OrganizationSettingsResponse>(
      `/platform/tech-admin/organizations/${encodeURIComponent(organizationId)}/settings`
    );
  },
  updateAdminOrganizationSetting(
    organizationId: string,
    settingKey: string,
    body: UpdateOrganizationSettingRequest
  ) {
    return apiRequest<OrganizationSettingsResponse>(
      `/platform/tech-admin/organizations/${encodeURIComponent(organizationId)}/settings/${encodeURIComponent(settingKey)}`,
      { method: "PUT", body }
    );
  },
  createAdminConnection(body: { organization_id: string; connected_organization_id: string }) {
    return apiRequest("/platform/tech-admin/connections", { method: "POST", body });
  },
  initiateAdminImportUpload(body: {
    organization_id: string;
    import_type: AdminImportType;
    original_file_name: string;
    mime_type: string;
    file_size_bytes: number;
  }) {
    return apiRequest<AdminFileUploadInitResponse>("/platform/tech-admin/import-uploads", { method: "POST", body });
  },
  completeAdminImportUpload(fileUploadId: string, organizationId: string) {
    return apiRequest(`/platform/tech-admin/import-uploads/${fileUploadId}/complete`, {
      method: "POST",
      body: { organization_id: organizationId }
    });
  },
  createAdminImport(body: { organization_id: string; import_type: AdminImportType; file_upload_id: string }) {
    return apiRequest<AdminMasterDataUploadResponse>("/platform/tech-admin/imports", { method: "POST", body });
  },
  createAdminStaffImport(body: { organization_id: string; file_upload_id: string }) {
    return apiRequest<AdminMasterDataUploadResponse>("/platform/tech-admin/staff-imports", { method: "POST", body });
  }
};
